import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@nexus/ui";
import type { InsightsTrend } from "../../types/preflight.types";
import { TONE_BG, TONE_TEXT } from "../../constants/result-state";
import {
  SCORE_BAND_NEEDS_REVIEW_MIN,
  SCORE_BAND_STRONG_MIN,
  bandTone,
  scoreToBand,
  type BandTone,
} from "../../lib/score-band";
import { canAnimate } from "../../lib/motion";
import { WidgetHelp } from "../WidgetHelp/WidgetHelp";

// Design tokens are hex values — use var() directly; hsl(var(...)) resolves to black in SVG.
const CHART_AXIS_TICK = {
  fontSize: 10,
  fill: "var(--muted-foreground)",
} as const;
const CHART_SERIES_STROKE = "var(--primary)";
const CHART_SURFACE = "var(--card)";
const SCORE_AXIS_DOMAIN: [number, number] = [0, 100];
const SCORE_AXIS_MAX = 100;

/** The band thresholds double as the only y-ticks — the chart is read by zone, not by grid. */
const SCORE_AXIS_TICKS = [
  0,
  SCORE_BAND_NEEDS_REVIEW_MIN,
  SCORE_BAND_STRONG_MIN,
  SCORE_AXIS_MAX,
];

const TONE_CHART_COLOR: Record<BandTone, string> = {
  success: "var(--success)",
  warning: "var(--warning)",
  error: "var(--error)",
};

/** Band zones tint the plot so a point's verdict reads from its height alone. */
const BAND_ZONES = [
  { tone: "error", from: 0, to: SCORE_BAND_NEEDS_REVIEW_MIN },
  {
    tone: "warning",
    from: SCORE_BAND_NEEDS_REVIEW_MIN,
    to: SCORE_BAND_STRONG_MIN,
  },
  { tone: "success", from: SCORE_BAND_STRONG_MIN, to: SCORE_AXIS_MAX },
] as const satisfies readonly { tone: BandTone; from: number; to: number }[];

const ZONE_FILL_OPACITY = 0.07;
const DOT_RADIUS = 6;
const DOT_LABEL_OFFSET = 11;
const ACTIVE_HALO_OFFSET = 3;
const ACTIVE_HALO_WIDTH = 4;
const ACTIVE_HALO_OPACITY = 0.3;

/** Band pill surface, matching the score rail's hover panel. */
const BAND_PILL: Record<BandTone, string> = {
  success: "bg-success-subtle text-success-subtle-foreground",
  warning: "bg-warning-subtle text-warning-subtle-foreground",
  error: "bg-error-subtle text-error-subtle-foreground",
};

/** Scores read as whole numbers everywhere in Pre Flight; a change carries its sign. */
function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : String(delta);
}

/** Matches the card's count-ups, so the whole overview settles at the same moment. */
const TREND_DRAW_MS = 700;

/** The aggregate's month buckets, e.g. `Jun 2026`. Report-name labels never match. */
const MONTH_YEAR_LABEL = /^(\w{3}) (\d{4})$/;

/**
 * Month labels drop the year while the whole series sits inside one, so the axis and the
 * range read as `Mar – Jun`. Returns null when the points aren't month buckets at all —
 * single-market scope labels them by report name, which must stay as-is.
 */
function compactMonthLabels(labels: string[]): string[] | null {
  const parsed = labels.map((label) => label.match(MONTH_YEAR_LABEL));
  if (parsed.some((match) => match == null)) return null;

  const matches = parsed as RegExpMatchArray[];
  const years = new Set(matches.map((match) => match[2]));
  return years.size === 1 ? matches.map((match) => match[1]) : labels;
}

function formatMonthYear(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

interface ChartRow {
  /** Axis text — the month without its year where the series allows it. */
  label: string;
  /** Always carries the year, so the screen-reader list stays unambiguous. */
  fullLabel: string;
  avgScore: number;
  creativeCount: number;
  /** Points gained or lost since the previous period; null on the first point. */
  change: { delta: number; fromLabel: string } | null;
}

function rowTone(row: ChartRow): BandTone {
  return bandTone(scoreToBand(row.avgScore));
}

/** Recharts hands the renderer a partial props bag; only these fields are needed. */
interface PointRenderProps {
  cx?: number;
  cy?: number;
  index?: number;
  points?: unknown[];
  payload?: ChartRow;
}

/** Where along the draw this point sits, so it lands as the line reaches it. */
function pointRevealDelayMs(index: number, pointCount: number): number {
  if (pointCount < 2) return 0;
  return Math.round((index / (pointCount - 1)) * TREND_DRAW_MS);
}

/**
 * A point and its score, drawn together so the score always travels with its dot. The
 * score sits above the dot in the band colour, so the line needs no axis reading.
 */
function TrendPoint({ cx, cy, index = 0, points, payload }: PointRenderProps) {
  if (cx == null || cy == null || !payload) return null;
  const color = TONE_CHART_COLOR[rowTone(payload)];
  const delayMs = pointRevealDelayMs(index, points?.length ?? 0);

  return (
    <g
      className="preflight-trend-point"
      style={
        { "--preflight-trend-point-delay": `${delayMs}ms` } as CSSProperties
      }
    >
      <circle
        cx={cx}
        cy={cy}
        r={DOT_RADIUS}
        fill={color}
        stroke={CHART_SURFACE}
        strokeWidth={2}
      />
      <text
        x={cx}
        y={cy - DOT_LABEL_OFFSET}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill={color}
      >
        {payload.avgScore}
      </text>
    </g>
  );
}

/** A hovered point reads as a halo rather than a bigger dot, so the score label stays put. */
function TrendActivePoint({ cx, cy, payload }: PointRenderProps) {
  if (cx == null || cy == null || !payload) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={DOT_RADIUS + ACTIVE_HALO_OFFSET}
      fill="none"
      stroke={TONE_CHART_COLOR[rowTone(payload)]}
      strokeOpacity={ACTIVE_HALO_OPACITY}
      strokeWidth={ACTIVE_HALO_WIDTH}
    />
  );
}

/** Recharts passes the hovered row inside its payload list. */
interface TrendTooltipProps {
  active?: boolean;
  payload?: { payload: ChartRow }[];
}

/**
 * What a point means, on hover: the period it covers, its average in band terms, how many
 * creatives it averages, and the move since the previous period — the context the score
 * label alone cannot carry. Decorative, since the point list below states the same values.
 */
function TrendTooltip({ active, payload }: TrendTooltipProps) {
  const { t } = useTranslation();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;

  const band = scoreToBand(row.avgScore);
  const tone = bandTone(band);

  return (
    <div className="bg-popover text-popover-foreground border-border flex flex-col gap-1 rounded-md border px-3 py-2.5 shadow-lg">
      <div className="flex items-center gap-3">
        <span className="text-foreground text-sm font-semibold">
          {row.fullLabel}
        </span>
        <span
          className={cn(
            "ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
            BAND_PILL[tone],
          )}
        >
          <span
            aria-hidden
            className={cn("size-1.5 rounded-full", TONE_BG[tone])}
          />
          {t(`preflight.band.${band}`)}
        </span>
      </div>

      <span
        className={cn(
          "text-lg leading-none font-semibold tabular-nums",
          TONE_TEXT[tone],
        )}
      >
        {row.avgScore}
        <span className="text-muted-foreground text-xs font-medium">
          {" "}
          {t("preflight.marketOverview.scoreScale")}
        </span>
      </span>

      <span className="text-muted-foreground flex flex-wrap items-center gap-1 text-[11px]">
        <span className="tabular-nums">
          {t("preflight.marketOverview.trendTooltipCreatives", {
            count: row.creativeCount,
          })}
        </span>
        {row.change ? (
          <>
            <span aria-hidden>{t("preflight.middot")}</span>
            <span className="tabular-nums">
              {t("preflight.marketOverview.trendTooltipChange", {
                delta: formatDelta(row.change.delta),
                label: row.change.fromLabel,
              })}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

export interface AvgScoreTrendProps {
  /** The series as the aggregate returned it — one point per report or per month (R20). */
  trend: InsightsTrend;
  className?: string;
}

/**
 * Average score over time for the current scope (R20). The series is rendered as the
 * aggregate bucketed it, so the hub never re-buckets; the plot is banded by score zone so
 * a point's verdict reads from its height. Alongside the title sit the range the series
 * covers and the delta against the first point. The points are also listed as text for
 * screen readers, since the line itself carries no accessible values.
 */
export function AvgScoreTrend({ trend, className }: AvgScoreTrendProps) {
  const { t } = useTranslation();

  const { points, from, to } = trend;
  const axisLabels = compactMonthLabels(points.map((point) => point.label));
  const rows: ChartRow[] = points.map((point, index) => {
    const previous = points[index - 1];
    const avgScore = Math.round(point.avgScore);
    return {
      label: axisLabels?.[index] ?? point.label,
      fullLabel: point.label,
      avgScore,
      creativeCount: point.creativeCount,
      change: previous
        ? {
            delta: avgScore - Math.round(previous.avgScore),
            fromLabel: axisLabels?.[index - 1] ?? previous.label,
          }
        : null,
    };
  });

  // Month buckets name the range from the series itself, so the header and the axis agree;
  // report-name points fall back to the dates the aggregate reports.
  const [fromLabel, toLabel] = axisLabels
    ? [axisLabels.at(0) ?? null, axisLabels.at(-1) ?? null]
    : [from ? formatMonthYear(from) : null, to ? formatMonthYear(to) : null];
  const rangeLabel =
    fromLabel && toLabel && fromLabel !== toLabel
      ? t("preflight.marketOverview.trendRange", "{{from}} – {{to}}", {
          from: fromLabel,
          to: toLabel,
        })
      : (fromLabel ?? toLabel);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-foreground text-sm font-semibold">
          {t("preflight.marketOverview.trendTitle", "Average score trend")}
        </h2>
        {rangeLabel ? (
          <span className="text-muted-foreground/80 text-xs tabular-nums">
            {rangeLabel}
          </span>
        ) : null}
        <WidgetHelp topic="scoreTrend" className="-my-1 ml-auto" />
      </div>

      {rows.length > 0 ? (
        <>
          <div aria-hidden className="h-44 w-full sm:h-49">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={rows}
                margin={{ top: 18, right: 12, bottom: 0, left: 0 }}
                // The plot is aria-hidden behind the point list below, so Recharts' keyboard
                // layer would only add a focus stop that announces nothing.
                accessibilityLayer={false}
              >
                {BAND_ZONES.map((zone) => (
                  <ReferenceArea
                    key={zone.tone}
                    y1={zone.from}
                    y2={zone.to}
                    fill={TONE_CHART_COLOR[zone.tone]}
                    fillOpacity={ZONE_FILL_OPACITY}
                    stroke="none"
                    ifOverflow="extendDomain"
                  />
                ))}
                {BAND_ZONES.filter((zone) => zone.from > 0).map((zone) => (
                  <ReferenceLine
                    key={zone.tone}
                    y={zone.from}
                    stroke={TONE_CHART_COLOR[zone.tone]}
                    strokeOpacity={0.5}
                    strokeDasharray="4 4"
                  />
                ))}
                <XAxis
                  dataKey="label"
                  tick={CHART_AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={SCORE_AXIS_DOMAIN}
                  ticks={SCORE_AXIS_TICKS}
                  tick={CHART_AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                />
                <Tooltip
                  content={<TrendTooltip />}
                  cursor={{
                    stroke: "var(--border)",
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  isAnimationActive={false}
                  wrapperStyle={{ outline: "none" }}
                />
                <Line
                  dataKey="avgScore"
                  type="linear"
                  stroke={CHART_SERIES_STROKE}
                  strokeWidth={2}
                  dot={<TrendPoint />}
                  activeDot={<TrendActivePoint />}
                  isAnimationActive={canAnimate()}
                  animationDuration={TREND_DRAW_MS}
                  animationEasing="linear"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <ul
            className="sr-only"
            aria-label={t(
              "preflight.marketOverview.trendPointsAria",
              "Average score by period",
            )}
          >
            {rows.map((row) => (
              <li key={row.fullLabel}>
                {t(
                  "preflight.marketOverview.trendPoint",
                  "{{label}}: {{score}} out of 100",
                  { label: row.fullLabel, score: row.avgScore },
                )}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {t(
            "preflight.marketOverview.trendEmpty",
            "No scored creatives in this scope yet.",
          )}
        </p>
      )}
    </div>
  );
}
